import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, fadeIn, viewportOnce } from '../lib/animations';

const ContactPage: React.FC = () => {
  return (
    <section className="mx-auto max-w-3xl px-4 py-20">
      <motion.h1
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="text-4xl font-bold tracking-tight text-gradient"
      >
        Get In Touch
      </motion.h1>
      <motion.p
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="mt-6 text-base leading-relaxed text-white/70 max-w-prose"
      >
        The fastest way to reach me is email. I&apos;m also active on LinkedIn. Feel free to say hello or start a conversation about systems, modernization, or developer experience.
      </motion.p>
  <div className="mt-10 space-y-5">
        <EmailRow />
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="flex items-center gap-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/15 ring-1 ring-inset ring-brand-400/30">
            {/* LinkedIn SVG */}
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-brand-300" aria-hidden="true" fill="currentColor">
              <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5.001 2.5 2.5 0 0 1 0-5.001ZM3 9h4v12H3zM14.25 9c-2.071 0-3.25 1.138-3.25 2.429V21h-4V9h4v1.203C11.39 9.677 12.659 9 14.25 9c2.485 0 4.5 1.622 4.5 5.06V21h-4v-6.5c0-1.52-.78-2.5-2-2.5Z" />
            </svg>
          </span>
          <a
            href="https://www.linkedin.com/in/cbachicha/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/90 hover:text-white transition text-sm font-medium underline-offset-4 hover:underline"
          >
            linkedin.com/in/cbachicha
          </a>
        </motion.div>
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="flex items-center gap-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/15 ring-1 ring-inset ring-brand-400/30">
            {/* GitHub SVG */}
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-brand-300" aria-hidden="true" fill="currentColor">
              <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.554 2 12.118c0 4.464 2.865 8.243 6.839 9.583.5.095.682-.219.682-.486 0-.24-.009-.876-.014-1.72-2.782.615-3.369-1.357-3.369-1.357-.455-1.174-1.11-1.487-1.11-1.487-.908-.63.069-.617.069-.617 1.003.072 1.531 1.05 1.531 1.05.892 1.549 2.341 1.101 2.91.842.091-.655.35-1.101.636-1.354-2.22-.258-4.555-1.135-4.555-5.051 0-1.116.39-2.03 1.03-2.746-.103-.258-.447-1.296.098-2.701 0 0 .84-.27 2.75 1.048A9.33 9.33 0 0 1 12 6.844c.85.004 1.705.116 2.504.34 1.909-1.318 2.747-1.048 2.747-1.048.546 1.405.202 2.443.1 2.701.64.716 1.028 1.63 1.028 2.746 0 3.926-2.339 4.79-4.566 5.044.359.318.678.944.678 1.905 0 1.374-.013 2.48-.013 2.818 0 .269.18.585.688.485A10.12 10.12 0 0 0 22 12.118C22 6.554 17.522 2 12 2Z" />
            </svg>
          </span>
          <a
            href="https://github.com/cbachich"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/90 hover:text-white transition text-sm font-medium underline-offset-4 hover:underline"
          >
            github.com/cbachich
          </a>
        </motion.div>
        <motion.div
          variants={fadeInUp}
          initial="hidden"
            whileInView="visible"
          viewport={viewportOnce}
          aria-hidden="true"
          className="relative pt-10"
        >
          <div className="mx-auto relative h-44 w-44 sm:h-52 sm:w-52 flex items-center justify-center">
            {/* Pulsing concentric rings */}
            <span className="absolute inset-0 rounded-full bg-brand-400/10 ring-1 ring-inset ring-brand-400/30 animate-[ringPulse_4.5s_ease-in-out_infinite]" />
            <span className="absolute inset-3 rounded-full bg-brand-400/5 ring-1 ring-inset ring-brand-300/25 animate-[ringPulse_4.5s_ease-in-out_infinite] [animation-delay:1.4s]" />
            <span className="absolute inset-6 rounded-full bg-brand-300/5 ring-1 ring-inset ring-brand-200/20 animate-[ringPulse_4.5s_ease-in-out_infinite] [animation-delay:2.8s]" />
            {/* Envelope icon container */}
            <div className="relative z-10 h-16 w-20 sm:h-20 sm:w-24 rounded-xl bg-gradient-to-br from-brand-500/25 to-brand-400/10 backdrop-blur-md ring-1 ring-brand-400/40 shadow-glow flex items-center justify-center animate-[floatY_6s_ease-in-out_infinite]">
              <svg viewBox="0 0 48 48" aria-hidden="true" className="h-10 w-10 text-brand-200 drop-shadow">
                <path fill="currentColor" fillRule="evenodd" d="M6 14.5A4.5 4.5 0 0 1 10.5 10h27A4.5 4.5 0 0 1 42 14.5v19a4.5 4.5 0 0 1-4.5 4.5h-27A4.5 4.5 0 0 1 6 33.5v-19Zm5.287-.934A1.5 1.5 0 0 0 9.713 16l12.83 9.023a3 3 0 0 0 3.414 0L38.787 16a1.5 1.5 0 0 0-1.574-2.434L25.957 22.06a3 3 0 0 1-3.914 0L11.287 13.566Z" clipRule="evenodd" />
              </svg>
              <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-brand-500 text-[10px] font-semibold text-white flex items-center justify-center shadow-glow animate-[pulseSoft_5s_ease-in-out_infinite]">
                ✦
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactPage;

// Email row with copy-to-clipboard
const EmailRow: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const email = 'cbachich@gmail.com';
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // swallow
    }
  };
  return (
    <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="flex items-center gap-4">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/15 ring-1 ring-inset ring-brand-400/30 text-brand-300 font-semibold">@</span>
      <a
        href={`mailto:${email}`}
        className="text-white/90 hover:text-white transition text-sm font-medium underline-offset-4 hover:underline"
      >
        {email}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="ml-2 inline-flex items-center gap-1 rounded-md bg-white/5 px-2.5 py-1.5 text-[11px] font-medium text-white/70 ring-1 ring-inset ring-white/10 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        aria-label="Copy email to clipboard"
      >
        <span>{copied ? 'Copied' : 'Copy'}</span>
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {copied ? (
            <path d="M5 13l4 4L19 7" />
          ) : (
            <>
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4c0-1.1.9-2 2-2h9a2 2 0 0 1 2 2v1" />
            </>
          )}
        </svg>
      </button>
    </motion.div>
  );
};
